import React, {useCallback, useEffect, useState} from 'react'
import {Box, ThemeProvider, useMediaQuery, Button} from '@mui/material'
import {theme} from '../../../theme/DashboardTheme'
import CenteredTabs from '../../Views/Tabs'
import RemovalStats from './RemovalStats'
import OptOuts from './OptOuts'
import {useAppDispatch, useAppSelector} from '../../../redux/storeExports'
import {selectActiveGrant} from 'shared/redux/product/ProductSlice'
import {fetchOptOuts, selectOptOuts, fetchBrokers} from "shared/redux/peopleFinder/PeopleFinderSlice";
import AnalyticsManager from '../../../services/AnalyticsManager'

const DashboardGrid = () => {
  const [tabValue, setTabValue] = useState('Removal Stats')
  const dispatch = useAppDispatch()
  const grant = useAppSelector(selectActiveGrant)
  const optouts = useAppSelector(selectOptOuts)
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue)
    AnalyticsManager.trackTypedEvent({
      event: 'Page View - Web',
      path: tabValue==='Opt Out Requests' ? '/dashboard/OptOutTab' : '/dashboard/RemovalsTab',
      search: '',
      source: 'Web App'
    })
  }
  const [isFetchingOptOuts, setIsFetchingOptOuts] = useState(true)
  const [isFetchingBrokers, setIsFetchingBrokers] = useState(true)
  const fetchOptoutResults = useCallback(async () => {
    setIsFetchingOptOuts(true)
    await dispatch(fetchOptOuts())
    setIsFetchingOptOuts(false)
  }, [dispatch])

  const fetchBrokerResults = useCallback(async () => {
    setIsFetchingBrokers(true)
    await dispatch(fetchBrokers())
    setIsFetchingBrokers(false)
  },[dispatch])

  useEffect(() => {
    fetchOptoutResults()
    fetchBrokerResults()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ToDo: We will remove this check once blast emails are sent out to all those subscribers who have purchased a grant before 2025-01-21
  const isGrantDateValid =
    !grant?.active ||
    (grant?.grant_purchase_date &&
      new Date(grant.grant_purchase_date) > new Date('2025-01-21') || optouts.length > 0)

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Box
      sx={{
        mb: isMobile ? '20px' : 0,
        '& .MuiCircularProgress-root': {
          color: '#1976D2',
          marginTop: 40,
        },
      }}>
        <ThemeProvider theme={theme}>
          {!isMobile ? (
            <CenteredTabs
              value={tabValue}
              handleChange={handleTabChange}
              isGrantDateValid={isGrantDateValid}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '51px',
                bgcolor: '#fff',
                borderRadius: '16px',
                p: 0.5,
                px:2,
                mt: 2,
                mb: 3,
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Button
                onClick={() => setTabValue('Removal Stats')}
                sx={{
                  borderRadius: '12px',
                  width: '50%',
                  height: '35px',
                  fontSize: { xs: '12px', '@media (max-width: 320px)': '10px' },
                  fontFamily: 'InterSemiBold',
                  textTransform: 'none',
                  bgcolor: tabValue === 'Removal Stats' ? '#7459D9' : 'transparent',
                  color: tabValue === 'Removal Stats' ? '#fff' : '#615F6B',
                  '&:hover': {
                    bgcolor: tabValue === 'Removal Stats' ? '#614BBA' : 'rgba(116, 89, 217, 0.05)',
                  },
                }}
              >
                Removal Stats
              </Button>
              <Button
                onClick={() => setTabValue('Opt Out Requests')}
                sx={{
                  border: 'none',
                  width: '50%',
                  borderRadius: '12px',
                  height: '35px',
                  fontSize: { xs: '11px', '@media (max-width: 320px)': '10px' },
                  fontFamily: 'InterSemiBold',
                  textTransform: 'none',
                  bgcolor: tabValue === 'Opt Out Requests' ? '#7459D9' : 'transparent',
                  color: tabValue === 'Opt Out Requests' ? '#fff' : '#615F6B',
                  '&:hover': {
                    bgcolor: tabValue === 'Opt Out Requests' ? '#614BBA' : 'rgba(116, 89, 217, 0.05)',
                  },
                }}
              >
                Opt Out Requests
              </Button>
            </Box>
          )}

          {tabValue === 'Removal Stats' ? (
            <RemovalStats grant={grant} isLoading={isFetchingBrokers}/>
          ) : (
            <OptOuts grant={grant} optouts={optouts} isLoading={isFetchingOptOuts}/>
          )}
        </ThemeProvider>
    </Box>
  )
}

export default DashboardGrid
